import APILibrary, { ClassExtender, GConstructor, GenerateResource } from './APILibrary';
import { Base, Collection } from 'active-resource';
import { ConversationMemberResource } from 'util/resources/ConversationMember';
import { MessageResource } from 'util/resources/Message';
import { UserProfileResource } from 'util/resources/UserProfile';

/**
 * Relationship classes
 */
export class ConversationAttributes {
  id: string = null;
  unreadCount: number = null;
  updatedAt: string = null;
  latestMessageAt: string = null;
  starred: boolean = null;
}

export class ConversationBelongsTo {}
export class ConversationHasOne {}

export class ConversationHasMany {
  messages: () => Collection<MessageResource> = () => new Collection<MessageResource>();
  conversationMembers: () => Collection<ConversationMemberResource> = () =>
    new Collection<ConversationMemberResource>();
}

/**
 * Utility interfaces
 */
export interface ConversationMethods {
  getUserProfile: () => UserProfileResource;
}

export interface ConversationInterface
  extends ConversationAttributes,
    ConversationHasMany,
    ConversationType {}

type ConversationType = GConstructor<ConversationInterface>;

export interface ConversationResource
  extends Base<ConversationAttributes>,
    ConversationInterface,
    ConversationMethods {}

/**
 * Resource methods
 */
const BaseClass = () =>
  GenerateResource<ConversationAttributes>({
    queryName: 'user_profile/conversations',
    className: 'Conversation',
    AttributesClass: ConversationAttributes,
    HasManyClasses: [
      {
        Klass: ConversationHasMany,
      },
    ],
  });

export const ConversationResourceClass = <TBase extends ConversationType>(
  BaseConstructor: TBase,
): TBase =>
  class extends BaseConstructor implements ConversationMethods {
    getUserProfile() {
      const userProfile = this.conversationMembers()
        .toArray()
        ?.find((member) => member.messageableType === 'UserProfile')
        ?.messageable();
      return userProfile as UserProfileResource;
    }
  };

export class ConversationResource extends ClassExtender<ConversationInterface>(
  BaseClass,
  ConversationResourceClass,
) {}

/**
 * Resource instance
 */
export const Conversation = APILibrary.createResource<ConversationResource>(ConversationResource);
